
import React, { useEffect } from 'react';
import { format } from 'date-fns';
import { useBatchList } from '@/hooks/useBatches';
import DataTable, { Column } from '@/components/ui/DataTable';
import { InvoiceBatch } from '@/services/batchService';
import { formatCurrency } from '@/lib/utils';

const BatchList: React.FC = () => {
  const { batches, isLoading } = useBatchList();

  // Disable network polling for this component
  useEffect(() => {
    return () => {
      // Clean up any listeners or polling if needed
    };
  }, []);

  const formatDate = (dateString: string) => {
    return format(new Date(dateString), 'MMM d, yyyy HH:mm');
  };

  const getMonthName = (month: number) => {
    return new Date(0, month - 1).toLocaleString('default', { month: 'long' });
  };

  const columns: Column<InvoiceBatch>[] = [
    {
      key: 'batch_id',
      header: 'Batch ID',
      cell: (batch) => <span className="font-medium">#{batch.batch_id}</span>,
      sortable: true,
    },
    {
      key: 'created_at',
      header: 'Created At',
      cell: (batch) => formatDate(batch.created_at),
      sortable: true,
    },
    {
      key: 'period',
      header: 'Billing Period',
      cell: (batch) => `${getMonthName(batch.month)} ${batch.year}`,
      sortable: true,
    },
    {
      key: 'run_type',
      header: 'Type',
      cell: (batch) => (
        <span className={`px-2 py-1 rounded-full text-xs font-medium ${
          batch.run_type === 'test' ? 'bg-yellow-100 text-yellow-800' : 'bg-blue-100 text-blue-800'
        }`}>
          {batch.run_type === 'test' ? 'Test' : 'Live'}
        </span>
      ),
      sortable: true,
    },
    {
      key: 'total_invoices',
      header: 'Invoices',
      cell: (batch) => batch.total_invoices,
      sortable: true,
    },
    {
      key: 'total_amount',
      header: 'Total Amount',
      cell: (batch) => formatCurrency(batch.total_amount),
      sortable: true,
      className: 'text-right',
    },
  ];

  return (
    <div className="space-y-6">
      <div className="glass-card rounded-lg overflow-hidden">
        <DataTable
          data={batches}
          columns={columns}
          keyField="batch_id"
          isLoading={isLoading}
        />
      </div>
    </div>
  );
};

export default BatchList;
